/**
 * Billing Configuration Service
 *
 * Frontend service for managing project-level and assignment-level billing configurations.
 * Separate methods for each config type: discount, min_days, max_days
 */

import api from './api';

const billingConfigService = {
  // ===== PROJECT-LEVEL CONFIGS =====

  // Discount configs
  getProjectDiscountConfigs: async (projectCode) => {
    const response = await api.get(`/projects/${projectCode}/billing-configs/discount`);
    return response.data.configs;
  },

  createProjectDiscountConfig: async (projectCode, data) => {
    const response = await api.post(
      `/projects/${projectCode}/billing-configs/discount`,
      data
    );
    return response.data.config;
  },

  updateProjectDiscountConfig: async (projectCode, configId, data) => {
    const response = await api.put(
      `/projects/${projectCode}/billing-configs/discount/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteProjectDiscountConfig: async (projectCode, configId) => {
    await api.delete(`/projects/${projectCode}/billing-configs/discount/${configId}`);
  },

  // Min days configs
  getProjectMinDaysConfigs: async (projectCode) => {
    const response = await api.get(`/projects/${projectCode}/billing-configs/min-days`);
    return response.data.configs;
  },

  createProjectMinDaysConfig: async (projectCode, data) => {
    const response = await api.post(
      `/projects/${projectCode}/billing-configs/min-days`,
      data
    );
    return response.data.config;
  },

  updateProjectMinDaysConfig: async (projectCode, configId, data) => {
    const response = await api.put(
      `/projects/${projectCode}/billing-configs/min-days/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteProjectMinDaysConfig: async (projectCode, configId) => {
    await api.delete(`/projects/${projectCode}/billing-configs/min-days/${configId}`);
  },

  // Max days configs
  getProjectMaxDaysConfigs: async (projectCode) => {
    const response = await api.get(`/projects/${projectCode}/billing-configs/max-days`);
    return response.data.configs;
  },

  createProjectMaxDaysConfig: async (projectCode, data) => {
    const response = await api.post(
      `/projects/${projectCode}/billing-configs/max-days`,
      data
    );
    return response.data.config;
  },

  updateProjectMaxDaysConfig: async (projectCode, configId, data) => {
    const response = await api.put(
      `/projects/${projectCode}/billing-configs/max-days/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteProjectMaxDaysConfig: async (projectCode, configId) => {
    await api.delete(`/projects/${projectCode}/billing-configs/max-days/${configId}`);
  },

  // ===== ASSIGNMENT-LEVEL CONFIGS =====

  // Discount overrides
  getAssignmentDiscountConfigs: async (assignmentId) => {
    const response = await api.get(
      `/assignments/${assignmentId}/billing-configs/discount`
    );
    return response.data.configs;
  },

  createAssignmentDiscountConfig: async (assignmentId, data) => {
    const response = await api.post(
      `/assignments/${assignmentId}/billing-configs/discount`,
      data
    );
    return response.data.config;
  },

  updateAssignmentDiscountConfig: async (assignmentId, configId, data) => {
    const response = await api.put(
      `/assignments/${assignmentId}/billing-configs/discount/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteAssignmentDiscountConfig: async (assignmentId, configId) => {
    await api.delete(`/assignments/${assignmentId}/billing-configs/discount/${configId}`);
  },

  // Min days overrides
  getAssignmentMinDaysConfigs: async (assignmentId) => {
    const response = await api.get(
      `/assignments/${assignmentId}/billing-configs/min-days`
    );
    return response.data.configs;
  },

  createAssignmentMinDaysConfig: async (assignmentId, data) => {
    const response = await api.post(
      `/assignments/${assignmentId}/billing-configs/min-days`,
      data
    );
    return response.data.config;
  },

  updateAssignmentMinDaysConfig: async (assignmentId, configId, data) => {
    const response = await api.put(
      `/assignments/${assignmentId}/billing-configs/min-days/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteAssignmentMinDaysConfig: async (assignmentId, configId) => {
    await api.delete(`/assignments/${assignmentId}/billing-configs/min-days/${configId}`);
  },

  // Max days overrides
  getAssignmentMaxDaysConfigs: async (assignmentId) => {
    const response = await api.get(
      `/assignments/${assignmentId}/billing-configs/max-days`
    );
    return response.data.configs;
  },

  createAssignmentMaxDaysConfig: async (assignmentId, data) => {
    const response = await api.post(
      `/assignments/${assignmentId}/billing-configs/max-days`,
      data
    );
    return response.data.config;
  },

  updateAssignmentMaxDaysConfig: async (assignmentId, configId, data) => {
    const response = await api.put(
      `/assignments/${assignmentId}/billing-configs/max-days/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteAssignmentMaxDaysConfig: async (assignmentId, configId) => {
    await api.delete(`/assignments/${assignmentId}/billing-configs/max-days/${configId}`);
  },

  // Bill rate overrides (date-ranged bill rates for assignments)
  getAssignmentBillRateConfigs: async (assignmentId) => {
    const response = await api.get(
      `/assignments/${assignmentId}/billing-configs/bill-rate`
    );
    return response.data.configs;
  },

  createAssignmentBillRateConfig: async (assignmentId, data) => {
    const response = await api.post(
      `/assignments/${assignmentId}/billing-configs/bill-rate`,
      data
    );
    return response.data.config;
  },

  updateAssignmentBillRateConfig: async (assignmentId, configId, data) => {
    const response = await api.put(
      `/assignments/${assignmentId}/billing-configs/bill-rate/${configId}`,
      data
    );
    return response.data.config;
  },

  deleteAssignmentBillRateConfig: async (assignmentId, configId) => {
    await api.delete(`/assignments/${assignmentId}/billing-configs/bill-rate/${configId}`);
  },
};

export default billingConfigService;
